import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiClock,
  FiExternalLink,
  FiEye,
  FiFilter,
  FiGlobe,
  FiImage,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiVideo,
  FiX,
} from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import {
  saveArtistProfessionalProfile,
  subscribeArtistAdminProfiles,
  toggleArtistPortfolioModeration,
  updateArtistFeaturedVisibility,
  updateArtistVerificationDecision,
} from "../services/artistProfileAdminService";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b45309",
  danger: "#b91c1c",
  info: "#2563eb",
};

const VERIFICATION_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "hold", label: "Hold" },
  { value: "rejected", label: "Rejected" },
  { value: "removed", label: "Removed" },
  { value: "approved", label: "Verified" },
];

const FEATURED_OPTIONS = [
  { value: "all", label: "All artists" },
  { value: "featured", label: "Featured" },
  { value: "standard", label: "Standard" },
];

const COMPLETENESS_OPTIONS = [
  { value: "all", label: "All completeness" },
  { value: "high", label: "80% and above" },
  { value: "mid", label: "50% to 79%" },
  { value: "low", label: "Below 50%" },
];

const MEDIA_OPTIONS = [
  { value: "all", label: "All media" },
  { value: "flagged", label: "Flagged or hidden" },
  { value: "clean", label: "Clean only" },
];

const SOCIAL_FIELDS = [
  { key: "website", label: "Website" },
  { key: "instagram", label: "Instagram" },
  { key: "youtube", label: "YouTube" },
  { key: "facebook", label: "Facebook" },
  { key: "spotify", label: "Spotify" },
  { key: "soundcloud", label: "SoundCloud" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "twitter", label: "Twitter" },
];

const AVAILABILITY_OPTIONS = ["", "Available", "Limited", "Busy", "Unavailable"];
const TRAVEL_OPTIONS = [
  "",
  "Local only",
  "Within city",
  "Domestic travel",
  "International travel",
  "Flexible",
];

export default function ArtistVerification({ mode = "verification" }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedArtistId = searchParams.get("artistId") || "";

  const employee = useMemo(() => safeGetEmployee(), []);
  const role = String(employee?.role || "").toLowerCase();
  const permissions = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const isSuperAdmin = role === "super_admin";

  const canViewVerification =
    isSuperAdmin ||
    permissions.some((permission) =>
      [
        "view_artist_verification",
        "verify_artist",
        "manage_artists",
        "manage_artist_pro_profiles",
        "moderate_artist_media",
      ].includes(permission)
    );
  const canViewVerified =
    isSuperAdmin ||
    permissions.some((permission) =>
      [
        "view_verified_artists",
        "view_artists",
        "manage_artists",
        "manage_artist_pro_profiles",
        "moderate_artist_media",
        "feature_artists",
      ].includes(permission)
    );
  const canView = mode === "verification" ? canViewVerification : canViewVerified;
  const canVerify =
    isSuperAdmin ||
    permissions.some((permission) => ["verify_artist", "manage_artists"].includes(permission));
  const canEditProfile =
    isSuperAdmin ||
    permissions.some((permission) =>
      ["manage_artists", "manage_artist_pro_profiles", "edit_artist"].includes(permission)
    );
  const canModerateMedia =
    isSuperAdmin ||
    permissions.some((permission) => ["manage_artists", "moderate_artist_media"].includes(permission));
  const canFeature =
    isSuperAdmin ||
    permissions.some((permission) => ["manage_artists", "feature_artists", "verify_artist"].includes(permission));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filters, setFilters] = useState(() => getInitialFilters(mode));
  const [profileForm, setProfileForm] = useState(emptyProfileForm());
  const [actionBusy, setActionBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    setFilters(getInitialFilters(mode));
    setSearch("");
  }, [mode]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const unsubscribe = subscribeArtistAdminProfiles(
      (nextRows) => {
        setRows(nextRows);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Artist admin stream failed:", err);
        setLoading(false);
        setError(resolveFirestoreMessage(err, "Unable to load artist profiles right now."));
      }
    );

    return () => unsubscribe();
  }, [canView, refreshNonce]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const categoryOptions = useMemo(
    () => ["all", ...uniqueValues(rows.flatMap((row) => row.category))],
    [rows]
  );
  const cityOptions = useMemo(
    () => ["all", ...uniqueValues(rows.map((row) => row.city))],
    [rows]
  );

  const filteredRows = useMemo(() => {
    let list = [...rows];
    const query = deferredSearch.trim().toLowerCase();

    if (query) {
      list = list.filter((artist) => artist.searchText.includes(query));
    }

    if (filters.verification !== "all") {
      list = list.filter((artist) => artist.verificationState === filters.verification);
    }

    if (filters.category !== "all") {
      list = list.filter((artist) => artist.category.includes(filters.category));
    }

    if (filters.city !== "all") {
      list = list.filter((artist) => artist.city === filters.city);
    }

    if (filters.featured !== "all") {
      list = list.filter((artist) =>
        filters.featured === "featured" ? artist.isFeatured : !artist.isFeatured
      );
    }

    if (filters.completeness !== "all") {
      list = list.filter((artist) => matchesCompletenessFilter(artist, filters.completeness));
    }

    if (filters.media !== "all") {
      list = list.filter((artist) =>
        filters.media === "flagged" ? artist.flaggedMediaCount > 0 : artist.flaggedMediaCount === 0
      );
    }

    return list.sort(mode === "verification" ? sortForVerificationQueue : sortForVerifiedManagement);
  }, [deferredSearch, filters, mode, rows]);

  useEffect(() => {
    if (!filteredRows.length) {
      if (selectedArtistId) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("artistId");
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    if (!selectedArtistId || !filteredRows.some((artist) => artist.id === selectedArtistId)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("artistId", filteredRows[0].id);
      setSearchParams(nextParams, { replace: true });
    }
  }, [filteredRows, searchParams, selectedArtistId, setSearchParams]);

  const selectedArtist = useMemo(() => {
    if (!selectedArtistId) return filteredRows[0] || null;
    return rows.find((artist) => artist.id === selectedArtistId) || filteredRows[0] || null;
  }, [filteredRows, rows, selectedArtistId]);
  const isVerifiedMode = mode === "verified";
  const useSingleFlow = true;

  useEffect(() => {
    if (!selectedArtist) {
      setProfileForm(emptyProfileForm());
      return;
    }

    setProfileForm(buildProfileForm(selectedArtist));
  }, [selectedArtist]);

  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((artist) => artist.verificationState === "pending").length;
    const verified = rows.filter((artist) => artist.verificationState === "approved").length;
    const featured = rows.filter((artist) => artist.isFeatured).length;
    const flaggedMedia = rows.reduce((sum, artist) => sum + Number(artist.flaggedMediaCount || 0), 0);
    const averageCompletion = total
      ? Math.round(rows.reduce((sum, artist) => sum + Number(artist.profileCompletionScore || 0), 0) / total)
      : 0;

    return { total, pending, verified, featured, flaggedMedia, averageCompletion };
  }, [rows]);

  const adminContext = useMemo(
    () => ({
      docId: String(employee?.docId || ""),
      name:
        employee?.fullName ||
        employee?.full_name ||
        employee?.name ||
        employee?.mobile ||
        "Admin",
      role: String(employee?.role || ""),
    }),
    [employee]
  );

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setSearch("");
    setFilters(getInitialFilters(mode));
  }

  function openArtist(artistId) {
    if (!artistId) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("artistId", artistId);
    setSearchParams(nextParams, { replace: true });
  }

  function updateProfileField(key, value) {
    setProfileForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateSocialField(key, value) {
    setProfileForm((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [key]: value },
    }));
  }

  async function handleSaveProfile() {
    if (!selectedArtist) return;
    if (!canEditProfile) {
      showToast("You do not have permission to update professional profile fields.", "error");
      return;
    }

    try {
      setActionBusy(`profile:${selectedArtist.id}`);
      await saveArtistProfessionalProfile(selectedArtist.id, profileForm, adminContext);
      showToast(`Professional profile saved for ${selectedArtist.stageName}.`, "success");
    } catch (err) {
      console.error("Artist professional profile save failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to save artist professional profile."), "error");
    } finally {
      setActionBusy("");
    }
  }

  async function handleFeaturedToggle(nextValue) {
    if (!selectedArtist) return;
    if (!canFeature) {
      showToast("You do not have permission to change featured visibility.", "error");
      return;
    }

    try {
      setActionBusy(`feature:${selectedArtist.id}`);
      await updateArtistFeaturedVisibility(selectedArtist.id, nextValue, adminContext);
      showToast(
        `${selectedArtist.stageName} is ${nextValue ? "now" : "no longer"} featured.`,
        "success"
      );
    } catch (err) {
      console.error("Artist featured update failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update featured visibility."), "error");
    } finally {
      setActionBusy("");
    }
  }

  async function handlePortfolioAction(media, action) {
    if (!selectedArtist || !media) return;
    if (!canModerateMedia) {
      showToast("You do not have permission to moderate portfolio media.", "error");
      return;
    }

    try {
      setActionBusy(`${action}:${media.id}`);
      await toggleArtistPortfolioModeration(selectedArtist, media.id, action, adminContext);
      showToast(
        `${media.title} ${action === "hide" ? "hidden" : action === "unhide" ? "restored" : action === "flag" ? "flagged" : "unflagged"}.`,
        "success"
      );
    } catch (err) {
      console.error("Portfolio moderation failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update media moderation."), "error");
    } finally {
      setActionBusy("");
    }
  }

  function askVerificationAction(action) {
    if (!selectedArtist) return;
    setConfirm({
      title: `${formatAction(action)} Verification`,
      message: buildVerificationConfirmMessage(action, selectedArtist.stageName),
      confirmText: formatAction(action),
      run: () => handleVerificationAction(action),
    });
  }

  async function handleVerificationAction(action) {
    if (!selectedArtist) return;
    if (!canVerify) {
      showToast("You do not have permission to review verification requests.", "error");
      return;
    }

    try {
      setActionBusy(`verify:${selectedArtist.id}:${action}`);
      await updateArtistVerificationDecision(selectedArtist.id, action, adminContext);
      setConfirm(null);
      showToast(buildVerificationSuccessMessage(action, selectedArtist.stageName), "success");
    } catch (err) {
      console.error("Artist verification update failed:", err);
      showToast(resolveFirestoreMessage(err, "Unable to update verification state."), "error");
    } finally {
      setActionBusy("");
    }
  }

  function showToast(message, type = "success") {
    setToast({ message, type });
  }

  if (!canView) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={34} color="#e53935" />
          <h3 style={{ margin: "1rem 0 0.35rem", color: COLORS.text }}>Access Denied</h3>
          <p style={{ margin: 0, color: COLORS.subtext }}>
            You do not have permission to access artist verification management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={heroCard}>
        <div style={heroCopy}>
          <div style={heroIcon}>
            <FiShield />
          </div>
          <div>
            <h2 style={heroTitle}>
              {mode === "verification"
                ? "Artist Verification Requests"
                : "Verified Artist + Media Kit Management"}
            </h2>
            <p style={heroSub}>
              {mode === "verification"
                ? "Review artist verification requests and approve the right profiles."
                : "Manage verified artists, featured visibility, and media quality."}
            </p>
          </div>
        </div>

        <div style={statsGrid}>
          <StatCard label="Artists" value={stats.total} tone="neutral" compact />
          <StatCard label="Pending" value={stats.pending} tone="warn" compact />
          <StatCard label="Verified" value={stats.verified} tone="ok" compact />
          <StatCard label="Featured" value={stats.featured} tone="brand" compact />
          <StatCard label="Flagged" value={stats.flaggedMedia} tone="danger" compact />
          <StatCard label="Completion" value={`${stats.averageCompletion}%`} tone="info" compact />
        </div>
      </div>

      <div style={tabRow}>
        <button
          type="button"
          style={mode === "verification" ? tabButtonActive : tabButton}
          onClick={() => navigate("/panel/artist-verification")}
        >
          <FiShield />
          <span>Verification Requests</span>
        </button>
        <button
          type="button"
          style={mode === "verified" ? tabButtonActive : tabButton}
          onClick={() => navigate("/panel/verified-artists")}
        >
          <FaCheckCircle />
          <span>Verified Artists</span>
        </button>
      </div>

      <div style={controlsCard}>
        <div style={controlsRow}>
          <div style={{ ...searchBox, flex: 1 }}>
            <FiSearch style={{ color: COLORS.subtext }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search artist name, stage name, city, category, genre, social link, UID..."
              style={searchInput}
            />
          </div>

          <button
            type="button"
            style={ghostButton}
            onClick={() => setRefreshNonce((value) => value + 1)}
          >
            <FiRefreshCw />
            <span>Refresh</span>
          </button>

          <button type="button" style={ghostButton} onClick={resetFilters}>
            <FiFilter />
            <span>Reset filters</span>
          </button>
        </div>

        <div style={filterGrid}>
          <FilterSelect
            label="Verification"
            value={filters.verification}
            onChange={(value) => setFilter("verification", value)}
            options={VERIFICATION_OPTIONS}
            compact
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            onChange={(value) => setFilter("category", value)}
            options={categoryOptions}
            compact
          />
          <FilterSelect
            label="City"
            value={filters.city}
            onChange={(value) => setFilter("city", value)}
            options={cityOptions}
            compact
          />
          <FilterSelect
            label="Featured"
            value={filters.featured}
            onChange={(value) => setFilter("featured", value)}
            options={FEATURED_OPTIONS}
            compact
          />
          <FilterSelect
            label="Completeness"
            value={filters.completeness}
            onChange={(value) => setFilter("completeness", value)}
            options={COMPLETENESS_OPTIONS}
            compact
          />
          <FilterSelect
            label="Media"
            value={filters.media}
            onChange={(value) => setFilter("media", value)}
            options={MEDIA_OPTIONS}
            compact
          />
        </div>
      </div>

      {error ? (
        <div style={errorCard}>
          <FaExclamationCircle />
          <span>{error}</span>
        </div>
      ) : null}

      <div style={useSingleFlow ? verifiedFlowLayout : isVerifiedMode ? verifiedFlowLayout : layoutGrid}>
        {loading || filteredRows.length > 0 ? (
        <div style={useSingleFlow ? queueCardWide : isVerifiedMode ? queueCardWide : queueCard}>
          <div style={sectionHead}>
            <div>
              <div style={sectionTitle}>
                {mode === "verification" ? "Review Queue" : "Artist Directory"}
              </div>
              <div style={sectionCopy}>
                {filteredRows.length} artist{filteredRows.length === 1 ? "" : "s"} match the
                current filters.
              </div>
            </div>
          </div>

          {loading ? (
            <EmptyState
              title="Loading artist profiles"
              copy="Syncing artists and verification data from Firestore."
            />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title="No artist profiles found"
              copy="Try a broader filter or search term to load more artists."
            />
          ) : (
            <div style={isVerifiedMode ? queueListWide : queueList}>
              {filteredRows.map((artist) => {
                const selected = selectedArtist?.id === artist.id;
                return (
                  <button
                    key={artist.id}
                    type="button"
                    style={selected ? queueItemActive : queueItem}
                    onClick={() => openArtist(artist.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={queueTitle}>{artist.stageName}</div>
                        <div style={queueMeta}>
                          {artist.artistName} • {artist.city}
                        </div>
                      </div>
                      <FiArrowRight />
                    </div>

                    <div style={queueBadgeRow}>
                      <Badge tone={badgeToneForVerification(artist.verificationState)}>
                        {artist.verificationLabel}
                      </Badge>
                      <Badge tone={artist.isFeatured ? "brand" : "neutral"}>
                        {artist.isFeatured ? "Featured" : "Standard"}
                      </Badge>
                      <Badge tone={artist.flaggedMediaCount > 0 ? "danger" : "ok"}>
                        {artist.flaggedMediaCount > 0 ? `${artist.flaggedMediaCount} flagged` : "Media clean"}
                      </Badge>
                    </div>

                    <div style={queueFoot}>
                      <span>{artist.categoryLabel}</span>
                      <span>{artist.profileCompletionScore}% complete</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        ) : null}

        <div style={useSingleFlow ? detailCardWide : isVerifiedMode ? detailCardWide : detailCard}>
          <div style={sectionHead}>
            <div>
              <div style={sectionTitle}>
                {isVerifiedMode ? "Verified Artist Review Workspace" : "Artist Pro Profile Detail"}
              </div>
              <div style={sectionCopy}>
                Review verification, inspect profile completeness, edit professional fields, and moderate portfolio media.
              </div>
            </div>
            {selectedArtist ? (
              <Badge tone={badgeToneForVerification(selectedArtist.verificationState)}>
                {selectedArtist.verificationLabel}
              </Badge>
            ) : null}
          </div>
          {loading ? (
            <EmptyState
              title="Loading artist detail"
              copy="Select an artist to review the pro profile and media kit."
            />
          ) : !selectedArtist ? (
            <EmptyState
              title={mode === "verification" ? "No verification requests right now" : "No artist selected"}
              copy={
                mode === "verification"
                  ? "No artists match the current verification filters. Try changing filters or switch to Verified Artists."
                  : "Choose an artist from the list to open the full admin review workspace."
              }
            />
          ) : (
            <div style={detailBody}>
              <div style={detailHero}>
                <div style={detailHeroCopy}>
                  <div style={avatarWrap}>
                    {selectedArtist.coverImage ? (
                      <img src={selectedArtist.coverImage} alt={selectedArtist.stageName} style={avatarImage} />
                    ) : (
                      <span>{selectedArtist.stageName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <h3 style={detailTitle}>{selectedArtist.stageName}</h3>
                    <p style={detailSub}>
                      {selectedArtist.artistName} • {selectedArtist.categoryLabel} • {selectedArtist.city}
                    </p>
                  </div>
                </div>

                <div style={detailBadgeWrap}>
                  <Badge tone={badgeToneForVerification(selectedArtist.verificationState)}>
                    {selectedArtist.verificationLabel}
                  </Badge>
                  <Badge tone={selectedArtist.isFeatured ? "brand" : "neutral"}>
                    {selectedArtist.isFeatured ? "Featured" : "Standard"}
                  </Badge>
                  <Badge tone={selectedArtist.flaggedMediaCount > 0 ? "danger" : "ok"}>
                    {selectedArtist.portfolioCount} media
                  </Badge>
                </div>
              </div>

              <div style={actionPanel}>
                <div style={sectionTitle}>Admin Actions</div>
                <div style={actionGrid}>
                  <ActionButton
                    label="Approve"
                    tone="ok"
                    disabled={!canVerify || actionBusy.startsWith(`verify:${selectedArtist.id}`)}
                    onClick={() => askVerificationAction("approve")}
                  />
                  <ActionButton
                    label="Reject"
                    tone="danger"
                    disabled={!canVerify || actionBusy.startsWith(`verify:${selectedArtist.id}`)}
                    onClick={() => askVerificationAction("reject")}
                  />
                  <ActionButton
                    label="Remove Verification"
                    tone="warn"
                    disabled={!canVerify || actionBusy.startsWith(`verify:${selectedArtist.id}`)}
                    onClick={() => askVerificationAction("remove")}
                  />
                  <ActionButton
                    label={selectedArtist.isFeatured ? "Unfeature Artist" : "Feature Artist"}
                    tone="brand"
                    disabled={!canFeature || actionBusy === `feature:${selectedArtist.id}`}
                    onClick={() => handleFeaturedToggle(!selectedArtist.isFeatured)}
                  />
                </div>
              </div>

              <div style={metricsGrid}>
                <MetricCard label="Price Range" value={selectedArtist.priceRangeLabel} icon={<FiFilter />} />
                <MetricCard label="Portfolio" value={`${selectedArtist.portfolioCount} items`} icon={<FiImage />} />
                <MetricCard label="Social Links" value={`${selectedArtist.socialLinkCount} shared`} icon={<FiGlobe />} />
                <MetricCard label="Response Time" value={selectedArtist.responseTimeText || "Not shared"} icon={<FiClock />} />
                <MetricCard label="Availability" value={selectedArtist.availabilityStatus || "Not shared"} icon={<FiCalendar />} />
                <MetricCard label="Updated" value={formatDateTime(selectedArtist.updatedAt || selectedArtist.createdAt)} icon={<FiCalendar />} />
              </div>

              <div style={completenessCard}>
                <div style={sectionHead}>
                  <div>
                    <div style={sectionTitle}>Profile Completeness</div>
                    <div style={sectionCopy}>
                      This score is based on key professional fields used for discovery and trust.
                    </div>
                  </div>
                  <Badge tone={completionTone(selectedArtist.profileCompletionScore)}>
                    {selectedArtist.profileCompletionScore}%
                  </Badge>
                </div>
                <div style={progressTrack}>
                  <div
                    style={{
                      ...progressFill,
                      width: `${selectedArtist.profileCompletionScore}%`,
                      background: progressColor(selectedArtist.profileCompletionScore),
                    }}
                  />
                </div>
                {selectedArtist.missingProfileFields.length ? (
                  <div style={missingWrap}>
                    {selectedArtist.missingProfileFields.map((field) => (
                      <Badge key={field} tone="warn">
                        Missing: {formatLabel(field)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 12, color: COLORS.ok, fontWeight: 800 }}>
                    All core professional fields are present.
                  </div>
                )}
              </div>

              <div style={editorCard}>
                <div style={sectionHead}>
                  <div>
                    <div style={sectionTitle}>Professional Profile Editor</div>
                    <div style={sectionCopy}>
                      Update pro profile fields when an artist submission needs cleanup or moderation.
                    </div>
                  </div>
                  <button
                    type="button"
                    style={primaryButton}
                    disabled={!canEditProfile || actionBusy === `profile:${selectedArtist.id}`}
                    onClick={handleSaveProfile}
                  >
                    <FiCheck />
                    <span>
                      {actionBusy === `profile:${selectedArtist.id}` ? "Saving..." : "Save profile fields"}
                    </span>
                  </button>
                </div>

                <div style={formGrid}>
                  <Field label="Stage Name">
                    <input
                      value={profileForm.stageName}
                      onChange={(event) => updateProfileField("stageName", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="City">
                    <input
                      value={profileForm.city}
                      onChange={(event) => updateProfileField("city", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Availability Status">
                    <select
                      value={profileForm.availabilityStatus}
                      onChange={(event) => updateProfileField("availabilityStatus", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    >
                      {AVAILABILITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option || "Select availability"}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Travel Availability">
                    <select
                      value={profileForm.travelAvailability}
                      onChange={(event) => updateProfileField("travelAvailability", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    >
                      {TRAVEL_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option || "Select travel availability"}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Price Range Min">
                    <input
                      type="number"
                      value={profileForm.priceRangeMin}
                      onChange={(event) => updateProfileField("priceRangeMin", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Price Range Max">
                    <input
                      type="number"
                      value={profileForm.priceRangeMax}
                      onChange={(event) => updateProfileField("priceRangeMax", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Genres">
                    <input
                      value={profileForm.genres}
                      onChange={(event) => updateProfileField("genres", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Languages">
                    <input
                      value={profileForm.languages}
                      onChange={(event) => updateProfileField("languages", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Specialities">
                    <input
                      value={profileForm.specialities}
                      onChange={(event) => updateProfileField("specialities", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Response Time">
                    <input
                      value={profileForm.responseTimeText}
                      onChange={(event) => updateProfileField("responseTimeText", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Cover Image">
                    <input
                      value={profileForm.coverImage}
                      onChange={(event) => updateProfileField("coverImage", event.target.value)}
                      style={inputStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Media Kit Summary" full>
                    <textarea
                      value={profileForm.mediaKitSummary}
                      onChange={(event) => updateProfileField("mediaKitSummary", event.target.value)}
                      style={textareaStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Professional Bio" full>
                    <textarea
                      value={profileForm.professionalBio}
                      onChange={(event) => updateProfileField("professionalBio", event.target.value)}
                      style={textareaStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Past Events" full>
                    <textarea
                      value={profileForm.pastEvents}
                      onChange={(event) => updateProfileField("pastEvents", event.target.value)}
                      style={textareaStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                  <Field label="Past Venues" full>
                    <textarea
                      value={profileForm.pastVenues}
                      onChange={(event) => updateProfileField("pastVenues", event.target.value)}
                      style={textareaStyle}
                      disabled={!canEditProfile}
                    />
                  </Field>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={sectionTitle}>Social Links</div>
                  <div style={socialGrid}>
                    {SOCIAL_FIELDS.map((field) => (
                      <Field key={field.key} label={field.label}>
                        <input
                          value={profileForm.socialLinks[field.key] || ""}
                          onChange={(event) => updateSocialField(field.key, event.target.value)}
                          style={inputStyle}
                          disabled={!canEditProfile}
                          placeholder="https://..."
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              </div>

              <div style={infoCard}>
                <div style={sectionTitle}>Submitted Social Links</div>
                <div style={socialPreviewWrap}>
                  {SOCIAL_FIELDS.filter((field) => selectedArtist.socialLinks[field.key]).length ? (
                    SOCIAL_FIELDS.filter((field) => selectedArtist.socialLinks[field.key]).map((field) => (
                      <a
                        key={field.key}
                        href={selectedArtist.socialLinks[field.key]}
                        target="_blank"
                        rel="noreferrer"
                        style={linkChip}
                      >
                        <FiExternalLink />
                        <span>{field.label}</span>
                      </a>
                    ))
                  ) : (
                    <div style={{ color: COLORS.subtext }}>No social links submitted.</div>
                  )}
                </div>
              </div>

              <div style={infoCard}>
                <div style={sectionTitle}>Past Events and Venues</div>
                <div style={chipSection}>
                  {selectedArtist.pastEvents.length ? (
                    selectedArtist.pastEvents.map((item) => (
                      <Badge key={`event-${item}`} tone="neutral">
                        Event: {item}
                      </Badge>
                    ))
                  ) : (
                    <span style={mutedText}>No past events listed.</span>
                  )}
                </div>
                <div style={chipSection}>
                  {selectedArtist.pastVenues.length ? (
                    selectedArtist.pastVenues.map((item) => (
                      <Badge key={`venue-${item}`} tone="neutral">
                        Venue: {item}
                      </Badge>
                    ))
                  ) : (
                    <span style={mutedText}>No past venues listed.</span>
                  )}
                </div>
              </div>

              <div style={mediaCard}>
                <div style={sectionHead}>
                  <div>
                    <div style={sectionTitle}>Portfolio + Media Kit Moderation</div>
                    <div style={sectionCopy}>
                      Review every submitted image and video, then flag or hide any inappropriate content.
                    </div>
                  </div>
                  <Badge tone={selectedArtist.flaggedMediaCount > 0 ? "danger" : "ok"}>
                    {selectedArtist.flaggedMediaCount > 0
                      ? `${selectedArtist.flaggedMediaCount} media items need attention`
                      : "All media clean"}
                  </Badge>
                </div>

                {selectedArtist.portfolioMedia.length === 0 ? (
                  <EmptyState
                    title="No portfolio media"
                    copy="This artist has not submitted portfolio images or videos yet."
                  />
                ) : (
                  <div style={mediaGrid}>
                    {selectedArtist.portfolioMedia.map((media) => (
                      <div key={media.id} style={mediaItemCard}>
                        <div style={mediaPreviewWrap}>{renderMediaPreview(media)}</div>
                        <div style={{ padding: "0.85rem" }}>
                          <div style={{ fontWeight: 800, color: COLORS.text }}>{media.title}</div>
                          <div style={{ marginTop: 4, color: COLORS.subtext, fontSize: 13 }}>
                            {media.type === "image" ? "Portfolio image" : "Portfolio video"}
                          </div>
                          <div style={queueBadgeRow}>
                            <Badge tone={media.type === "image" ? "neutral" : "info"}>
                              {formatLabel(media.type)}
                            </Badge>
                            {media.isHidden ? <Badge tone="danger">Hidden</Badge> : null}
                            {media.isFlagged ? <Badge tone="warn">Flagged</Badge> : null}
                          </div>
                          <div style={mediaActionRow}>
                            <button
                              type="button"
                              style={smallActionGhost}
                              onClick={() => window.open(media.url, "_blank", "noopener,noreferrer")}
                            >
                              <FiEye />
                              <span>Open</span>
                            </button>
                            <button
                              type="button"
                              style={media.isHidden ? smallActionOk : smallActionWarn}
                              disabled={!canModerateMedia || actionBusy === `${media.isHidden ? "unhide" : "hide"}:${media.id}`}
                              onClick={() => handlePortfolioAction(media, media.isHidden ? "unhide" : "hide")}
                            >
                              {media.isHidden ? (
                                <>
                                  <FiCheck />
                                  <span>Unhide</span>
                                </>
                              ) : (
                                <>
                                  <FiX />
                                  <span>Hide</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              style={media.isFlagged ? smallActionOk : smallActionDanger}
                              disabled={!canModerateMedia || actionBusy === `${media.isFlagged ? "unflag" : "flag"}:${media.id}`}
                              onClick={() => handlePortfolioAction(media, media.isFlagged ? "unflag" : "flag")}
                            >
                              {media.isFlagged ? (
                                <>
                                  <FiCheck />
                                  <span>Unflag</span>
                                </>
                              ) : (
                                <>
                                  <FiAlertTriangle />
                                  <span>Flag</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Confirm"}
        loading={Boolean(confirm && actionBusy)}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.run?.()}
      />

      {toast ? (
        <div style={toast.type === "error" ? toastError : toastSuccess}>
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span>{toast.message}</span>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label style={{ ...fieldWrap, gridColumn: full ? "1 / -1" : "auto" }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, compact = false }) {
  const normalizedOptions = options.map((option) =>
    typeof option === "string"
      ? { value: option, label: option === "all" ? "All" : option }
      : option
  );

  return (
    <label style={compact ? filterLabelCompact : filterLabel}>
      <span style={compact ? filterCaptionCompact : filterCaption}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={compact ? filterSelectStyleCompact : filterSelectStyle}
      >
        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatCard({ label, value, tone = "neutral", compact = false }) {
  const palette = {
    neutral: { background: "#ffffff", border: "#e2e8f0", color: "#0f172a" },
    warn: { background: "#fff7ed", border: "#fdba74", color: "#b45309" },
    ok: { background: "#ecfdf5", border: "#bbf7d0", color: "#166534" },
    brand: { background: "#fff1f7", border: "#f9a8d4", color: "#be185d" },
    danger: { background: "#fef2f2", border: "#fecaca", color: "#b91c1c" },
    info: { background: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
  }[tone];

  return (
    <div
      style={{
        background: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: compact ? "0.55rem 0.7rem" : "0.8rem 0.95rem",
      }}
    >
      <div style={{ color: COLORS.subtext, fontSize: compact ? 11 : 12, fontWeight: 700 }}>{label}</div>
      <div style={{ marginTop: compact ? 4 : 6, color: palette.color, fontSize: compact ? 18 : 22, fontWeight: 900 }}>
        {value}
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon }) {
  return (
    <div style={metricCard}>
      <div style={metricLabel}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={metricValue}>{value || "Not shared"}</div>
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const palette = {
    neutral: { background: "#f8fafc", color: "#334155", border: "#e2e8f0" },
    ok: { background: "#ecfdf5", color: "#166534", border: "#bbf7d0" },
    warn: { background: "#fff7ed", color: "#b45309", border: "#fdba74" },
    danger: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
    brand: { background: "#fff1f7", color: "#be185d", border: "#f9a8d4" },
    info: { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  }[tone];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  );
}

function ActionButton({ label, onClick, disabled, tone = "neutral" }) {
  const palette = {
    ok: { background: "#16a34a", color: "#ffffff" },
    danger: { background: "#dc2626", color: "#ffffff" },
    warn: { background: "#ea580c", color: "#ffffff" },
    brand: { background: "linear-gradient(90deg,#f20c53,#fb6c33)", color: "#ffffff" },
    neutral: { background: "#ffffff", color: "#0f172a" },
  }[tone];

  return (
    <button
      type="button"
      style={{
        minHeight: 42,
        borderRadius: 12,
        border: tone === "neutral" ? `1px solid ${COLORS.border}` : "none",
        background: disabled ? "#e2e8f0" : palette.background,
        color: disabled ? "#64748b" : palette.color,
        padding: "0.75rem 0.9rem",
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function EmptyState({ title, copy }) {
  return (
    <div style={emptyState}>
      <div style={emptyIcon}>
        <FiAlertTriangle />
      </div>
      <div style={{ fontWeight: 900, color: COLORS.text }}>{title}</div>
      <div style={{ color: COLORS.subtext, maxWidth: 460 }}>{copy}</div>
    </div>
  );
}

function renderMediaPreview(media) {
  if (media.type === "image") {
    return <img src={media.thumbUrl || media.url} alt={media.title} style={mediaImage} />;
  }

  if (isDirectVideoUrl(media.url)) {
    return <video src={media.url} controls style={mediaVideo} />;
  }

  if (media.thumbUrl) {
    return <img src={media.thumbUrl} alt={media.title} style={mediaImage} />;
  }

  return (
    <div style={videoPlaceholder}>
      <FiVideo size={24} />
      <span>Video preview unavailable</span>
    </div>
  );
}

function getInitialFilters(mode) {
  return {
    verification: mode === "verification" ? "pending" : "approved",
    category: "all",
    city: "all",
    featured: "all",
    completeness: "all",
    media: "all",
  };
}

function emptyProfileForm() {
  return {
    stageName: "",
    city: "",
    availabilityStatus: "",
    travelAvailability: "",
    priceRangeMin: "",
    priceRangeMax: "",
    genres: "",
    languages: "",
    specialities: "",
    responseTimeText: "",
    coverImage: "",
    mediaKitSummary: "",
    professionalBio: "",
    pastEvents: "",
    pastVenues: "",
    socialLinks: SOCIAL_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {}),
  };
}

function buildProfileForm(artist) {
  return {
    stageName: artist.stageName || "",
    city: artist.city || "",
    availabilityStatus: artist.availabilityStatus || "",
    travelAvailability: artist.travelAvailability || "",
    priceRangeMin: artist.priceRangeMin || "",
    priceRangeMax: artist.priceRangeMax || "",
    genres: artist.genres.join(", "),
    languages: artist.languages.join(", "),
    specialities: artist.specialities.join(", "),
    responseTimeText: artist.responseTimeText || "",
    coverImage: artist.coverImage || "",
    mediaKitSummary: artist.mediaKitSummary || "",
    professionalBio: artist.professionalBio || "",
    pastEvents: artist.pastEvents.join(", "),
    pastVenues: artist.pastVenues.join(", "),
    socialLinks: { ...emptyProfileForm().socialLinks, ...artist.socialLinks },
  };
}

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function matchesCompletenessFilter(artist, value) {
  const score = Number(artist?.profileCompletionScore || 0);
  if (value === "high") return score >= 80;
  if (value === "mid") return score >= 50 && score < 80;
  if (value === "low") return score < 50;
  return true;
}

function sortForVerificationQueue(left, right) {
  const priority = {
    pending: 0,
    hold: 1,
    rejected: 2,
    removed: 3,
    approved: 4,
  };

  const stateGap =
    (priority[left?.verificationState] ?? 99) - (priority[right?.verificationState] ?? 99);
  if (stateGap !== 0) return stateGap;

  const flagGap = Number(right?.flaggedMediaCount || 0) - Number(left?.flaggedMediaCount || 0);
  if (flagGap !== 0) return flagGap;

  const completionGap =
    Number(left?.profileCompletionScore || 0) - Number(right?.profileCompletionScore || 0);
  if (completionGap !== 0) return completionGap;

  return Number(right?.updatedAtMs || right?.createdAtMs || 0) - Number(left?.updatedAtMs || left?.createdAtMs || 0);
}

function sortForVerifiedManagement(left, right) {
  const verifiedGap =
    Number(right?.verificationState === "approved") - Number(left?.verificationState === "approved");
  if (verifiedGap !== 0) return verifiedGap;

  const featuredGap = Number(right?.isFeatured) - Number(left?.isFeatured);
  if (featuredGap !== 0) return featuredGap;

  const flagGap = Number(right?.flaggedMediaCount || 0) - Number(left?.flaggedMediaCount || 0);
  if (flagGap !== 0) return flagGap;

  const completionGap =
    Number(right?.profileCompletionScore || 0) - Number(left?.profileCompletionScore || 0);
  if (completionGap !== 0) return completionGap;

  return Number(right?.updatedAtMs || right?.createdAtMs || 0) - Number(left?.updatedAtMs || left?.createdAtMs || 0);
}

function uniqueValues(values = []) {
  const lookup = new Map();
  values.forEach((entry) => {
    const text = String(entry ?? "").trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (!lookup.has(key)) lookup.set(key, text);
  });
  return Array.from(lookup.values()).sort((left, right) => left.localeCompare(right));
}

function badgeToneForVerification(state) {
  if (state === "approved") return "ok";
  if (state === "pending") return "warn";
  if (state === "hold") return "info";
  if (state === "rejected") return "danger";
  if (state === "removed") return "neutral";
  return "neutral";
}

function completionTone(score) {
  if (score >= 80) return "ok";
  if (score >= 50) return "info";
  return "warn";
}

function progressColor(score) {
  if (score >= 80) return "linear-gradient(90deg,#16a34a,#22c55e)";
  if (score >= 50) return "linear-gradient(90deg,#2563eb,#38bdf8)";
  return "linear-gradient(90deg,#ea580c,#f97316)";
}

function formatLabel(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();
}

function formatAction(action) {
  const map = {
    approve: "Approve",
    reject: "Reject",
    remove: "Remove",
  };
  return map[action] || formatLabel(action);
}

function buildVerificationConfirmMessage(action, stageName) {
  if (action === "approve") {
    return `Approve verification for ${stageName}? The verified badge and approved status will be visible in artist management.`;
  }
  if (action === "reject") {
    return `Reject verification for ${stageName}? The artist will stay unverified and appear as rejected in the admin review history.`;
  }
  return `Remove the verified badge for ${stageName}? This artist will move back into an unverified state.`;
}

function buildVerificationSuccessMessage(action, stageName) {
  if (action === "approve") return `${stageName} is now verified.`;
  if (action === "reject") return `${stageName} verification request was rejected.`;
  return `${stageName} is no longer marked as verified.`;
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "Not available";

  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function resolveFirestoreMessage(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("permission-denied")) return "You do not have permission to complete this admin action.";
  if (code.includes("unavailable")) return "Firestore is temporarily unavailable. Please try again.";
  if (code.includes("not-found")) return "The selected artist profile could not be found.";
  if (code.includes("failed-precondition")) {
    return "Firestore needs an additional index or precondition before this request can complete.";
  }
  return fallback;
}

function isDirectVideoUrl(url) {
  const value = String(url || "").trim().toLowerCase();
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/.test(value);
}

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

const pageWrap = {
  minHeight: "100vh",
  background: COLORS.page,
  padding: 16,
  display: "grid",
  gap: 16,
};

const heroCard = {
  background: "linear-gradient(135deg, rgba(242,12,83,0.98), rgba(251,108,51,0.94))",
  borderRadius: 20,
  padding: "0.9rem 1rem",
  color: "#ffffff",
  display: "grid",
  gap: 12,
  boxShadow: "0 18px 36px rgba(242,12,83,0.18)",
};

const heroCopy = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 12,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.16)",
  border: "1px solid rgba(255,255,255,0.22)",
  fontSize: 18,
};

const heroTitle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 900,
  letterSpacing: 0,
};

const heroSub = {
  margin: "0.25rem 0 0",
  maxWidth: 720,
  color: "rgba(255,255,255,0.88)",
  fontSize: 12,
  lineHeight: 1.45,
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(92px, 1fr))",
  gap: 8,
};

const tabRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const tabButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  minHeight: 44,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  fontWeight: 800,
  padding: "0.75rem 1rem",
  cursor: "pointer",
};

const tabButtonActive = {
  ...tabButton,
  border: "none",
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
  color: "#ffffff",
  boxShadow: "0 16px 32px rgba(242,12,83,0.18)",
};

const controlsCard = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  padding: "0.8rem",
  display: "grid",
  gap: 10,
  boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
};

const controlsRow = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const searchBox = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "0 0.75rem",
};

const searchInput = {
  flex: 1,
  minWidth: 180,
  border: "none",
  outline: "none",
  background: "transparent",
  color: COLORS.text,
  fontSize: 13,
};

const filterGrid = {
  display: "flex",
  flexWrap: "nowrap",
  gap: 8,
  overflowX: "auto",
  paddingBottom: 2,
};

const filterLabel = {
  display: "grid",
  gap: 6,
};

const filterCaption = {
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 800,
};

const filterSelectStyle = {
  minHeight: 44,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0 0.85rem",
  outline: "none",
  fontWeight: 700,
};

const filterLabelCompact = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  flex: "0 0 auto",
  whiteSpace: "nowrap",
};

const filterCaptionCompact = {
  color: COLORS.subtext,
  fontSize: 11,
  fontWeight: 800,
};

const filterSelectStyleCompact = {
  minHeight: 34,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0 0.65rem",
  outline: "none",
  fontWeight: 700,
  fontSize: 12,
};

const layoutGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(300px, 360px) minmax(0, 1fr)",
  gap: 16,
  alignItems: "start",
};

const verifiedFlowLayout = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gap: 18,
  alignItems: "start",
};

const queueCard = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 20,
  padding: "1rem",
  display: "grid",
  gap: 14,
  position: "sticky",
  top: 16,
  boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
};

const queueCardWide = {
  ...queueCard,
  position: "static",
  top: "auto",
  order: 2,
  borderRadius: 18,
  padding: "0.95rem",
  boxShadow: "0 18px 40px rgba(15,23,42,0.07)",
};

const detailCard = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 20,
  padding: "1rem",
  display: "grid",
  gap: 14,
  minWidth: 0,
  boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
};

const detailCardWide = {
  ...detailCard,
  order: 1,
  borderRadius: 20,
  padding: "1rem",
  boxShadow: "0 20px 48px rgba(15,23,42,0.08)",
};

const sectionHead = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const sectionTitle = {
  color: COLORS.text,
  fontSize: 18,
  fontWeight: 900,
};

const sectionCopy = {
  marginTop: 4,
  color: COLORS.subtext,
  fontSize: 13,
  lineHeight: 1.6,
  maxWidth: 760,
};

const queueList = {
  display: "grid",
  gap: 10,
  maxHeight: "calc(100vh - 290px)",
  overflowY: "auto",
  paddingRight: 4,
};

const queueListWide = {
  display: "grid",
  gap: 12,
  maxHeight: "none",
  overflowY: "visible",
  paddingRight: 0,
};

const queueItem = {
  width: "100%",
  textAlign: "left",
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  borderRadius: 16,
  padding: "0.95rem",
  display: "grid",
  gap: 10,
  cursor: "pointer",
  color: COLORS.text,
};

const queueItemActive = {
  ...queueItem,
  border: "1px solid rgba(242,12,83,0.22)",
  boxShadow: "0 18px 34px rgba(242,12,83,0.12)",
  background: "linear-gradient(180deg,#fff7fb 0%, #ffffff 100%)",
};

const queueTitle = {
  fontSize: 16,
  fontWeight: 900,
  color: COLORS.text,
};

const queueMeta = {
  marginTop: 4,
  color: COLORS.subtext,
  fontSize: 13,
  lineHeight: 1.5,
};

const queueBadgeRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const queueFoot = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 800,
};

const detailBody = {
  display: "grid",
  gap: 16,
};

const detailHero = {
  display: "flex",
  justifyContent: "space-between",
  gap: 14,
  flexWrap: "wrap",
  padding: "1rem",
  borderRadius: 18,
  border: `1px solid ${COLORS.border}`,
  background: "linear-gradient(180deg,#fff7fb 0%, #ffffff 100%)",
};

const detailHeroCopy = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  minWidth: 0,
};

const avatarWrap = {
  width: 72,
  height: 72,
  borderRadius: 20,
  overflow: "hidden",
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg,#f20c53,#fb6c33)",
  color: "#ffffff",
  fontSize: 28,
  fontWeight: 900,
  flexShrink: 0,
};

const avatarImage = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const detailTitle = {
  margin: 0,
  fontSize: 24,
  fontWeight: 900,
  color: COLORS.text,
};

const detailSub = {
  margin: "0.35rem 0 0",
  color: COLORS.subtext,
  lineHeight: 1.6,
};

const detailBadgeWrap = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignSelf: "flex-start",
};

const actionPanel = {
  display: "grid",
  gap: 12,
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: "1rem",
};

const actionGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
};

const metricsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
};

const metricCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  padding: "0.95rem",
  background: "#ffffff",
  display: "grid",
  gap: 8,
};

const metricLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 900,
};

const metricValue = {
  color: COLORS.text,
  fontSize: 16,
  fontWeight: 800,
  lineHeight: 1.4,
};

const completenessCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: "1rem",
  background: "#ffffff",
  display: "grid",
  gap: 12,
};

const progressTrack = {
  width: "100%",
  height: 12,
  borderRadius: 999,
  background: "#e2e8f0",
  overflow: "hidden",
};

const progressFill = {
  height: "100%",
  borderRadius: 999,
  transition: "width .25s ease",
};

const missingWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const editorCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: "1rem",
  background: "#ffffff",
  display: "grid",
  gap: 16,
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const fieldWrap = {
  display: "grid",
  gap: 6,
};

const fieldLabel = {
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 800,
};

const inputStyle = {
  minHeight: 44,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0 0.85rem",
  outline: "none",
  fontSize: 14,
};

const textareaStyle = {
  minHeight: 110,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0.75rem 0.85rem",
  outline: "none",
  fontSize: 14,
  resize: "vertical",
};

const socialGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
  marginTop: 10,
};

const infoCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: "1rem",
  background: "#ffffff",
  display: "grid",
  gap: 12,
};

const socialPreviewWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const linkChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "#f8fafc",
  padding: "0.6rem 0.9rem",
  color: COLORS.text,
  textDecoration: "none",
  fontWeight: 700,
};

const chipSection = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
};

const mutedText = {
  color: COLORS.subtext,
  fontSize: 13,
};

const mediaCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  padding: "1rem",
  background: "#ffffff",
  display: "grid",
  gap: 14,
};

const mediaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 12,
};

const mediaItemCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 18,
  overflow: "hidden",
  background: "#ffffff",
  display: "grid",
};

const mediaPreviewWrap = {
  aspectRatio: "16 / 10",
  background: "#0f172a",
  display: "grid",
  placeItems: "center",
};

const mediaImage = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const mediaVideo = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  background: "#000000",
};

const videoPlaceholder = {
  width: "100%",
  height: "100%",
  display: "grid",
  placeItems: "center",
  gap: 8,
  color: "#ffffff",
  fontWeight: 700,
};

const mediaActionRow = {
  marginTop: 12,
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const smallActionBase = {
  minHeight: 38,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  borderRadius: 10,
  padding: "0.55rem 0.8rem",
  fontWeight: 800,
  cursor: "pointer",
};

const smallActionGhost = {
  ...smallActionBase,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
};

const smallActionWarn = {
  ...smallActionBase,
  border: "1px solid #fdba74",
  background: "#fff7ed",
  color: "#b45309",
};

const smallActionDanger = {
  ...smallActionBase,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#b91c1c",
};

const smallActionOk = {
  ...smallActionBase,
  border: "1px solid #bbf7d0",
  background: "#ecfdf5",
  color: "#166534",
};

const primaryButton = {
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(90deg,#f20c53,#fb6c33)",
  color: "#ffffff",
  padding: "0.75rem 1rem",
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 16px 28px rgba(242,12,83,0.16)",
};

const ghostButton = {
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#ffffff",
  color: COLORS.text,
  padding: "0.75rem 1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const emptyState = {
  minHeight: 220,
  borderRadius: 18,
  border: `1px dashed ${COLORS.border}`,
  background: "#f8fafc",
  display: "grid",
  placeItems: "center",
  gap: 10,
  textAlign: "center",
  padding: "1.5rem",
};

const emptyIcon = {
  width: 56,
  height: 56,
  borderRadius: 18,
  display: "grid",
  placeItems: "center",
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  color: COLORS.warn,
  fontSize: 24,
};

const errorCard = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  color: "#b91c1c",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 14,
  padding: "0.85rem 1rem",
  fontWeight: 700,
};

const toastBase = {
  position: "fixed",
  right: 20,
  bottom: 20,
  zIndex: 10000,
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  borderRadius: 14,
  padding: "0.85rem 1rem",
  fontWeight: 800,
  boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
};

const toastSuccess = {
  ...toastBase,
  background: "#ecfdf5",
  color: "#166534",
  border: "1px solid #bbf7d0",
};

const toastError = {
  ...toastBase,
  background: "#fef2f2",
  color: "#b91c1c",
  border: "1px solid #fecaca",
};

const deniedWrap = {
  minHeight: "100vh",
  background: COLORS.page,
  padding: "2rem",
  display: "grid",
  placeItems: "center",
};

const deniedCard = {
  width: "100%",
  maxWidth: 440,
  borderRadius: 20,
  background: "#ffffff",
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 18px 36px rgba(15,23,42,0.12)",
  padding: "2rem",
  textAlign: "center",
};
